import { APP_CONFIG } from 'mangools-commons/dist/configs/app';
import ErrorCodes from 'mangools-commons/dist/constants/ErrorCodes';
import HumanService from 'mangools-commons/dist/services/HumanService';
import { propOr } from 'ramda';
import {
    computeTrendPercent,
    parseFloatWithNVFallback,
    parseIntWithNVFallback,
    parseLocationLabel,
    parseMSVWithoutCountDefault,
} from './parsers/commons';
import { ANY_LANGUAGE } from 'mangools-commons/dist/constants/Languages';

const ENDPOINT = '/gap-analysis';

type KeywordGapMode = 'domains' | 'pages';
const DEFAULT_MODE: KeywordGapMode = 'domains';

// Parsing functions
const parseMonthlySearchVolumes = (rawVolumes: any) => {
    if (!Array.isArray(rawVolumes)) return [];
    const parsed = rawVolumes.map((item: any) => ({
        count: parseIntWithNVFallback(item[2]),
        month: parseInt(item[1], 10),
        year: parseInt(item[0], 10),
    }));
    return parsed.sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
    });
};

const parseLocation = (rawLocation: any) => ({
    id: parseInt(rawLocation._id, 10),
    label: parseLocationLabel(rawLocation.label),
    code: rawLocation.code,
});

const parseKeywordData = (item: any) => {
    const msv = propOr([], 'msv', item);
    const monthlySearchVolumes = parseMonthlySearchVolumes(msv);
    const msvTrends = computeTrendPercent(parseMSVWithoutCountDefault(msv));

    return {
        _id: item._id,
        cpc: parseFloatWithNVFallback(item?.cpc),
        estimatedTraffic: parseIntWithNVFallback(item?.et),
        id: item._id, // eslint-disable-line no-underscore-dangle
        keyword: item.kw,
        keywordType: 0,
        language: ANY_LANGUAGE,
        location: parseLocation(item.location),
        monthlySearchVolumes,
        msvTrends,
        ppc: parseIntWithNVFallback(item?.ppc),
        rank: parseIntWithNVFallback(item?.seo),
        rankUpdatedAt: parseIntWithNVFallback(item?.seo_ts),
        searchCount: parseIntWithNVFallback(item?.sv),
        searchCountLatest: parseIntWithNVFallback(item?.sv_current),
        serpPosition: parseIntWithNVFallback(item?.position),
    };
};

// Parse function to transform API response data
const parse = (data: any) => {
    if (!data || !data.results) {
        return { results: [], isAllowedPagination: false, resultsSize: 0, mode: undefined };
    }

    const parsedResults = data.results.map((result: any) => ({
        ...result,
        keywords: result.keywords ? result.keywords.map(parseKeywordData) : [],
    }));

    return {
        ...data,
        results: parsedResults,
    };
};

// Type definitions based on API response
interface KeywordGapKeyword {
    _id: string;
    kw: string;
    lid: number;
    ts: number;
    svs: number;
    msv: number[][];
    ppc: number | null;
    sv: number;
    sv_current: number;
    cpc: number;
    seo: any | null;
    seo_ts: number | null;
    position: number;
    et: number;
    location: {
        _id: number;
        code: string;
        label: string;
    };
    rank?: number;
    ev?: number;
}

interface KeywordGapResult {
    keywords: KeywordGapKeyword[];
    totalCount: number;
    originalItemsCount: number;
    domain: string;
}

interface KeywordGapApiResponse {
    isAllowedPagination: boolean;
    resultsSize: number;
    results: KeywordGapResult[];
    mode?: KeywordGapMode;
}

interface KeywordGapRequestParams {
    mode?: KeywordGapMode | null;
    domain?: string;
    competitors?: string[];
    excludePages?: string | null;
    pages?: { [key: string]: string } | null;
    locationId: string | number;
    page?: number;
}

interface KeywordGapSourceParams {
    accessToken: string;
    params: KeywordGapRequestParams;
}

interface KeywordGapPayload {
    isAllowedPagination: boolean;
    resultsSize: number;
    results: KeywordGapResult[];
    mode?: KeywordGapMode;
    params: {
        mode: KeywordGapMode;
        domain: string | null;
        competitors: string[] | null;
        excludePages: string | null;
        pages: { [key: string]: string } | null;
        locationId: string | number;
        languageId?: string | number;
        page: number;
    };
}

interface KeywordGapSourceResponse {
    error: boolean;
    payload:
        | KeywordGapPayload
        | {
              requestURL: string;
              status: number;
              text: string;
              type?: string;
              retryAfter?: number | null;
          };
}

// Discriminated union prevents cross-mode field leakage at compile time;
// backend Joi uses `.unknown(false)` so any leak is a 422.
type DomainsModeBody = {
    mode: 'domains';
    domain: string;
    competitors: string[];
    location_id: number;
    page?: number;
};

type PagesModeBody = {
    mode: 'pages';
    exclude_pages: string;
    pages: { [key: string]: string };
    location_id: number;
    page?: number;
};

type KeywordGapRequestBody = DomainsModeBody | PagesModeBody;

class KeywordGapResultSource {
    static getData({ accessToken, params }: KeywordGapSourceParams): Promise<KeywordGapSourceResponse> {
        const {
            mode: rawMode,
            domain,
            competitors,
            excludePages,
            pages,
            locationId,
            page = 1,
        } = params;

        const mode: KeywordGapMode = rawMode === 'pages' ? 'pages' : DEFAULT_MODE;
        const locationIdInt = parseInt(locationId.toString(), 10);

        let requestBody: KeywordGapRequestBody;

        if (mode === 'pages') {
            // Backend Joi schema requires non-empty strings; strip empty slots from the params reducer.
            const filteredPages: { [key: string]: string } = {};
            if (pages && typeof pages === 'object') {
                for (const key of Object.keys(pages)) {
                    const value = pages[key];
                    if (typeof value === 'string' && value.trim() !== '') {
                        filteredPages[key] = value;
                    }
                }
            }

            requestBody = {
                mode: 'pages',
                exclude_pages: excludePages || '',
                pages: filteredPages,
                location_id: locationIdInt,
            };
        } else {
            requestBody = {
                mode: 'domains',
                domain: domain || '',
                competitors: (competitors || []).filter(
                    competitor => competitor && competitor.trim() !== '',
                ),
                location_id: locationIdInt,
            };
        }

        if (page > 1) {
            requestBody.page = page;
        }

        const requestURL = `${APP_CONFIG.API_HOST}${APP_CONFIG.API_PATH}${ENDPOINT}`;

        // All-mode echo keeps the saga's cache-skip equality keys stable across modes.
        const echoParams = {
            mode,
            domain: mode === 'domains' ? (domain ?? null) : null,
            competitors: mode === 'domains' ? (competitors ?? null) : null,
            excludePages: mode === 'pages' ? (excludePages ?? null) : null,
            pages: mode === 'pages' ? (pages ?? null) : null,
            locationId,
            page,
        };

        return fetch(requestURL, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'X-Access-Token': accessToken || '',
                'X-Human-Token': HumanService.gen(),
                'X-Mangools-Client': 'app',
            },
            body: JSON.stringify(requestBody),
        })
            .then(response => {
                if (response.ok) {
                    return response
                        .json()
                        .then((data: KeywordGapApiResponse) => {
                            const parsedData = parse(data);
                            return {
                                error: false,
                                payload: {
                                    isAllowedPagination: parsedData.isAllowedPagination,
                                    resultsSize: parsedData.resultsSize,
                                    results: parsedData.results,
                                    mode: (parsedData.mode as KeywordGapMode | undefined) ?? mode,
                                    params: echoParams,
                                },
                            };
                        })
                        .catch((ex: Error) => ({
                            error: true,
                            payload: {
                                requestURL,
                                status: ErrorCodes.PARSE_ERROR,
                                text: ex.toString(),
                            },
                        }));
                } else {
                    return response.json().then((e: any) => ({
                        error: true,
                        payload: {
                            requestURL,
                            status: response.status,
                            text: e.error ? e.error.message : null,
                            type: e.error ? e.error.type : null,
                            retryAfter: e.error ? e.error.retry_after : null,
                        },
                    }));
                }
            })
            .catch((ex: Error) => ({
                error: true,
                payload: {
                    requestURL,
                    status: ErrorCodes.FETCH_ERROR,
                    text: ex.toString(),
                },
            }));
    }
}

export default KeywordGapResultSource;
export { parse, DEFAULT_MODE };

export type {
    KeywordGapKeyword,
    KeywordGapMode,
    KeywordGapResult,
    KeywordGapApiResponse,
    KeywordGapRequestParams,
    KeywordGapSourceParams,
    KeywordGapPayload,
    KeywordGapSourceResponse,
};
